// IMPORTS
import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

// COMPONENTS
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorMessage from "../../components/ErrorMessage";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import PageHeadline from "../../components/PageHeadline";
import AreYouSureModal from "../../components/AreYouSureModal";
import InformationBox from "../../components/InformationBox";
import HotDropdownV2 from "../../components/HotdropdownV2";
import Dropdown from "../../components/Dropdown";
import StudentPaymentModal from "./StudentPaymentModal";
import AddStudentModal from "./AddStudentModal";
import EditStudentModal from "./EditStudentModal";
import StudentTransactionModal from "./StudentTransactionModal";
import CustomCheckbox from "../../components/CustomCheckbox";

// UTILS & SERVICES
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import { getAllStudents, deleteStudent } from "../../services/StudentServices";
import { getAllEducationLevels } from "../../services/educationLevelServices";

// ASSETS
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import transactionsIcon from "../../assets/transactions_icon.png";
import paymentIcon from "../../assets/payment_icon.png";
import addIcon from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import infoIcon from "../../assets/information.png";
import downloadIcon from "../../assets/download-icon.svg";
import { pdf } from "@react-pdf/renderer";
import StudentsStatementTemplate from "./StudentsStatementTemplate";
import useDebounce from "../../utils/useDebounce";
import PageWrapper from "../../components/PageWrapper";

// CONSTANTS
const SEARCH_TYPES = {
  STUDENT_NAME: "studentName",
  STUDENT_ID: "studentId",
  ACADEMIC_LEVEL: "academicLevel",
  RED_FLAG: "redFlag",
};

const INITIAL_DROPDOWN_STATE = { title: "", value: null };

export default function StudentsPage() {
  // HOOKS
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const { loading, setLoading } = useContext(DataContext);

  // STATES - Student Data
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);

  // STATES - Search & Filter
  const [searchType, setSearchType] = useState({
    title: t("studentName"),
    value: SEARCH_TYPES.STUDENT_NAME,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery)
  const [selectedAcademicLevel, setSelectedAcademicLevel] = useState(
    INITIAL_DROPDOWN_STATE,
  );
  const [selectedSubClass, setSelectedSubClass] = useState("");
  const [selectedRedFlag, setSelectedRedFlag] = useState(
    INITIAL_DROPDOWN_STATE,
  );
  const [includeGraduates, setIncludeGraduates] = useState(false);
  const [graduatesOnly, setGraduatesOnly] = useState(false);

  // STATES - Modal Control
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // STATES - UI Control
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [shouldRefetchStudents, setShouldRefetchStudents] = useState(false);

  // API HANDLERS
  const fetchEducationLevels = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm = "",
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm,
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
        schoolSubClasses:
          level.schoolSubClasses?.map((subClass) => ({
            title: subClass.name,
            value: subClass.id,
          })) || [],
      }));

      if (page === 1) {
        setEducationLevels(transformedData);
      } else {
        setEducationLevels((prev) => [...prev, ...transformedData]);
      }

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const fetchStudents = useCallback(async () => {
    const searchParams = {
      fullName:
        searchType.value === SEARCH_TYPES.STUDENT_NAME ? debouncedSearch : null,
      studentCode:
        searchType.value === SEARCH_TYPES.STUDENT_ID ? debouncedSearch : null,
      schoolClassId:
        searchType.value === SEARCH_TYPES.ACADEMIC_LEVEL &&
        selectedAcademicLevel.value
          ? selectedAcademicLevel.value
          : null,
      schoolSubClassId: selectedSubClass.value || null,
      includeGraduated: includeGraduates,
      areGraduates: !includeGraduates && graduatesOnly,
      redFlag:
        searchType.value === SEARCH_TYPES.RED_FLAG
          ? selectedRedFlag.value
          : null,
    };

    const response = await getAllStudents(
      pagination.currentPage,
      5,
      searchParams,
    );

    return response;
  }, [
    searchType.value,
    debouncedSearch,
    selectedAcademicLevel.value,
    selectedSubClass.value,
    selectedRedFlag.value,
    includeGraduates,
    graduatesOnly,
    pagination.currentPage,
  ]);

  const getStudents = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetchStudents();

      if (response.data?.students) {
        setStudents(response.data.students);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        }));

        if (response.data.students.length > 0) {
          setSelectedStudent(response.data.students[0]);
        } else {
          setSelectedStudent(null);
        }
      }
    } catch (error) {
      console.error("Failed to get students:", error);
      setErrorMessage(t("errorFetchingStudents"));
    } finally {
      setLoading(false);
    }
  }, [
    setLoading,
    t,
    fetchStudents
  ]);

  const handleDeleteStudent = async (studentId) => {
    try {
      setLoadingModal(true);
      const response = await deleteStudent(studentId);

      if (response.status === 200) {
        setShowDeleteConfirmation(false);
        await getStudents();
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      setErrorMessage(t("errorDeletingStudent"));
    } finally {
      setLoadingModal(false);
    }
  };

  // EVENT HANDLERS
  const handleSearchTypeChange = (selected) => {
    // Reset all search-related states
    setSearchType(selected);
    setSearchQuery("");
    setSelectedAcademicLevel(INITIAL_DROPDOWN_STATE);
    setSelectedSubClass("");
    setSelectedRedFlag(INITIAL_DROPDOWN_STATE);

    // Reset pagination to first page
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    // Load education levels if switching to academic level search
    if (selected.value === SEARCH_TYPES.ACADEMIC_LEVEL) {
      setEducationLevels([]);
      fetchEducationLevels(1, 5, false, "");
    }
  };

  const handleAcademicLevelChange = (selected) => {
    setSelectedAcademicLevel(selected);
    setSelectedSubClass("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSubClassChange = (selected) => {
    setSelectedSubClass(selected);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleRedFlagChange = (selected) => {
    setSelectedRedFlag(selected);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    getStudents();
  };

  const handleResetSearch = () => {
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleToggleTransactionsModal = (student) => {
    setShowTransactionsModal((prev) => !prev);
    if (student) {
      setSelectedStudent(student);
    }
  };

  const handleTogglePaymentModal = (student) => {
    setShowPaymentModal((prev) => !prev);
    if (student) {
      setSelectedStudent(student);
    }
  };

  const openEditStudentModal = (student) => {
    setSelectedStudent(student);
    setShowEditStudentModal(true);
  };

  // TABLE CONFIGURATION
  const titles = [
    { value: "student" },
    { value: "studentId", keepOnMobile: true },
    { value: "academicLevel", keepOnMobile: true },
    { value: "payment", keepOnMobile: true },
    { value: "transactions", keepOnMobile: true },
    { value: "ViewInfo", keepOnMobile: true },
  ];

  const rows = students.map((item) => [
    {
      isRedFlag: item?.studentEvaluations?.[0]?.redFlag || false,
      type: TABLE_CELL_TYPES.NO_RENDER,
    },
    {
      type: TABLE_CELL_TYPES.LINK,
      keepOnMobile: true,
      value: item?.fullName,
      to: `/student-profile/${item.id}`,
    },
    {
      value: item?.studentCode,
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: t(item?.activeYear?.schoolClass?.name || ""),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant="green"
          title={t("payment")}
          onClick={() => handleTogglePaymentModal(item)}
          icon={paymentIcon}
          iconSize={22}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant="blank"
          icon={transactionsIcon}
          iconSize={25}
          title={t("transactions")}
          onClick={() => handleToggleTransactionsModal(item)}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant="blank"
          icon={infoIcon}
          iconSize={19}
          title={t("viewStudentInfo")}
          onClick={() => openEditStudentModal(item)}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  // USE EFFECTS
  useEffect(() => {
    getStudents();
  }, [getStudents]);

  useEffect(() => {
    if (!showTransactionsModal && shouldRefetchStudents) {
      setShouldRefetchStudents(false);
      getStudents();
    }
  }, [showTransactionsModal, shouldRefetchStudents, getStudents]);

  // RENDER SEARCH INPUT BASED ON TYPE
  const renderSearchInput = () => {
    switch (searchType.value) {
      case SEARCH_TYPES.ACADEMIC_LEVEL:
        return (
          <div className="o-row o-row--gap-size_300 u-width_full-width o-column-row-switcher">
            <div className="o-column o-column--gap-size_200 u-width_full-width">
              <label>{t("academicLevel")}</label>
              <HotDropdownV2
                initOptions={[
                  ...educationLevels,
                  { title: t("selectAll"), value: null },
                ]}
                selected={selectedAcademicLevel.title || ""}
                setSelected={handleAcademicLevelChange}
                page="students"
                getData={fetchEducationLevels}
                optionsName="classes"
                thick
                flex
              />
            </div>
            <div className="o-column o-column--gap-size_200 u-width_full-width">
              <label>{t("subclass")}</label>
              <HotDropdownV2
                initOptions={[
                  ...(selectedAcademicLevel?.schoolSubClasses || []),
                  { title: t("selectAll"), value: null },
                ]}
                selected={selectedSubClass}
                setSelected={handleSubClassChange}
                thick
                flex
              />
            </div>
          </div>
        );

      case SEARCH_TYPES.RED_FLAG:
        return (
          <div className="o-column o-column--gap-size_200 u-width_full-width">
            <label>{t("status")}</label>
            <Dropdown
              options={[
                { title: t("yes"), value: true },
                { title: t("no"), value: false },
                { title: t("selectAll"), value: null },
              ]}
              selected={selectedRedFlag}
              setSelected={handleRedFlagChange}
              thick
              flex
            />
          </div>
        );

      default:
        return (
          <div className="o-column o-column--gap-size_200 u-width_full-width">
            <label>{t(searchType.value)}</label>
            <CustomInput
              placeholder={`${t("searchTitle")} ${t(searchType.value)}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              resetSearch={handleResetSearch}
              thick
              flex
            />
          </div>
        );
    }
  };

  // RENDER
  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
          withBackButton
      >
        <BackToDashboard
          title={t("back")}
          variant="blank-fit-content"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user" ? "/invoiceManagement" : "/school-dashboard"
          }
        />

        {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}

        <div className="o-row o-row--horizontal-align_space-between u-margin--top_700 u-margin--bottom_700">
          <PageHeadline title="students" />

          {userData?.userId && (
            <div className="o-row o-row--gap-size_300">
              <CustomButton
                title={t("createStudent")}
                icon={addIcon}
                iconSize={20}
                onClick={() => setShowAddStudentModal(true)}
              />
            </div>
          )}
        </div>

        {/* GRADUATE FILTERS */}
        <div className="o-column u-margin--bottom_400">
          <div className="o-row o-row--vertical-align_center">
            <CustomCheckbox
              checked={includeGraduates}
              onClick={() => {
                setIncludeGraduates((prev) => !prev);
                if (graduatesOnly) setGraduatesOnly(false);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
            />
            <span>{t("includeGraduateStudents")}</span>
          </div>

          <div className="o-row o-row--vertical-align_center">
            <CustomCheckbox
              checked={graduatesOnly}
              onClick={() => {
                setGraduatesOnly((prev) => !prev);
                if (includeGraduates) setIncludeGraduates(false);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
            />
            <span>{t("graduatesOnly")}</span>
          </div>
        </div>

        {/* SEARCH SECTION */}
        <section
          className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_start u-width_full-width"
          style={{ alignItems: "end" }}
        >
          <div className="o-row o-row--vertical-align_center o-column--vertical-align_start o-row--gap-size_300 u-width_full-width">
            {renderSearchInput()}
          </div>
          <div
            className="o-row o-row--horizontal-align_start o-row--gap-size_300 u-width_full-width"
            style={{
              alignItems: "end",
            }}
          >
            <div className="o-column o-column--gap-size_200">
              <label>{t("filter")}</label>
              <Dropdown
                options={[
                  { title: t("studentName"), value: SEARCH_TYPES.STUDENT_NAME },
                  { title: t("studentId"), value: SEARCH_TYPES.STUDENT_ID },
                  {
                    title: t("academicLevel"),
                    value: SEARCH_TYPES.ACADEMIC_LEVEL,
                  },
                  { title: t("redFlag"), value: SEARCH_TYPES.RED_FLAG },
                ]}
                selected={searchType}
                setSelected={handleSearchTypeChange}
                thick
                flex
              />
            </div>
            <CustomButton
              title={t("search")}
              onClick={handleSearch}
              icon={searchIcon}
              iconSize={15}
              classList="c-search-button"
            />
          </div>
        </section>

        {/* STUDENTS TABLE */}
        {loading ? (
          <Loader />
        ) : students.length === 0 ? (
          <InformationBox>
            <p>{t("noStudentsFound")}</p>
          </InformationBox>
        ) : (
          <ul className="users-list">
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <RoundedTable
              withRedFlag={true}
              titles={titles}
              rows={rows}
              fullData={students}
              pagination={pagination}
              setPagination={setPagination}
              selectedRows={selectedStudents}
              setSelectedRows={setSelectedStudents}
            />
          </ul>
        )}

        <div className="o-row o-row--horizontal-align_end u-margin--top_500 u-margin--bottom_400">
          <DownloadSudentStatementButton fetchStudents={fetchStudents} />
        </div>

        {/* MODALS */}
        <AddStudentModal
          isOpen={showAddStudentModal}
          onClose={() => setShowAddStudentModal(false)}
          onStudentCreated={getStudents}
          setPagination={setPagination}
        />

        <EditStudentModal
          isOpen={showEditStudentModal}
          onClose={() => setShowEditStudentModal(false)}
          student={selectedStudent}
          onStudentUpdated={getStudents}
          setPagination={setPagination}
        />

        {showTransactionsModal && (
          <StudentTransactionModal
            selected={selectedStudent}
            handleToggleTransactionsModal={handleToggleTransactionsModal}
            setShouldGetStudents={setShouldRefetchStudents}
          />
        )}

        {showPaymentModal && (
          <StudentPaymentModal
            selectedStudent={selectedStudent}
            handleTogglePaymentModal={handleTogglePaymentModal}
            refetchData={getStudents}
            setShouldGetStudents={setShouldRefetchStudents}
          />
        )}

        {showDeleteConfirmation && (
          <AreYouSureModal
            onClose={() => setShowDeleteConfirmation(false)}
            onConfirm={() => handleDeleteStudent(selectedStudent.id)}
            customMessage={t("deleteStudent")}
            closeTitle="cancel"
            confirmTitle="confirm"
            loading={loadingModal}
            cancel
            confirmIconType="whiteCheck"

          />
        )}
      </PageWrapper>
    </div>
  );
}

function DownloadSudentStatementButton({ fetchStudents }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  const handleDownloadStatement = async () => {
    setPdfLoading(true);

    try {
      const response = await fetchStudents();
        const students = response?.data?.students || [];
      if (students.length > 0) {
        // Generate the PDF with your custom component
        const blob = await pdf(
          <StudentsStatementTemplate
            language={currentDirection === "rtl" ? "AR" : "EN"}
            companyName={
              userData?.organizationName ||
              localStorage.getItem("organizationName") ||
              ""
            }
            schoolName={userData?.organizationName}
            students={students}
          />,
        ).toBlob();

        // Trigger browser download
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${t("studentsReport").replaceAll(" ", "-")
        }-${new Date().getMonth() + 1}-${new Date().getDate()}-${(
          Math.random() * 1000
        ).toFixed(0)}.pdf`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 0);
        document.body.removeChild(link);
      }

    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <CustomButton
      variant="blank"
      onClick={handleDownloadStatement}
      disabled={pdfLoading}
      icon={downloadIcon}
      title={pdfLoading ? t("generatingPdf") : t("downloadStudentsInfo")}
    />
  );
}
